import * as React from "react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    GetVapidPublicKey,
    SubscribeWebPush,
    UnsubscribeWebPush,
    GetWebPushSubscriptions,
    TestWebPush,
} from "./AccountStore";

interface Subscription {
    id: string;
    key: string;
    endpoint: string;
}

const ProfileNotifications = () => {
    const [supported, setSupported] = React.useState(false);
    const [permission, setPermission] = React.useState<NotificationPermission>("default");
    const [subscriptions, setSubscriptions] = React.useState<Subscription[]>([]);
    const [currentSubscription, setCurrentSubscription] = React.useState<PushSubscription | null>(null);
    const [loading, setLoading] = React.useState(true);
    const [actionLoading, setActionLoading] = React.useState(false);
    const [message, setMessage] = React.useState<{ text: string; success: boolean } | null>(null);

    React.useEffect(() => {
        const isSupported = "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
        setSupported(isSupported);

        if (isSupported) {
            setPermission(Notification.permission);
            registerServiceWorker().then(() => fetchSubscriptions());
        } else {
            setLoading(false);
        }
    }, []);

    const registerServiceWorker = async () => {
        try {
            await navigator.serviceWorker.register("/service-worker.js");
            const reg = await navigator.serviceWorker.ready;
            const existing = await reg.pushManager.getSubscription();
            setCurrentSubscription(existing);
        } catch (e) {
            console.error("Service worker registration failed", e);
        }
    };

    const fetchSubscriptions = async () => {
        try {
            const subs = await GetWebPushSubscriptions();
            setSubscriptions(subs || []);
        } catch {
            setSubscriptions([]);
        } finally {
            setLoading(false);
        }
    };

    const enablePushNotifications = async () => {
        setActionLoading(true);
        try {
            const perm = await Notification.requestPermission();
            setPermission(perm);

            if (perm !== "granted") {
                showMessage("Notification permission denied.", false);
                return;
            }

            const vapidPublicKey = await GetVapidPublicKey();
            if (!vapidPublicKey) {
                showMessage("Push notifications are not configured on this server.", false);
                return;
            }

            const reg = await navigator.serviceWorker.ready;

            // Clear any stale subscription before creating a fresh one.
            // Required when VAPID keys have been rotated: the old subscription
            // stored in the browser uses a different applicationServerKey, and
            // FCM will reject the new subscribe() if the old one isn't explicitly
            // removed first via an unsubscribe DELETE request.
            const stale = await reg.pushManager.getSubscription();
            if (stale) {
                await stale.unsubscribe();
            }

            const subscription = await reg.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
            });

            setCurrentSubscription(subscription);
            await SubscribeWebPush(subscription);
            await fetchSubscriptions();
            showMessage("Push notifications enabled successfully!", true);
        } catch (e) {
            showMessage("Failed to enable push notifications: " + (e as Error).message, false);
        } finally {
            setActionLoading(false);
        }
    };

    const disablePushNotifications = async () => {
        if (!currentSubscription) return;
        setActionLoading(true);
        try {
            await UnsubscribeWebPush(currentSubscription.endpoint);
            await currentSubscription.unsubscribe();
            setCurrentSubscription(null);
            await fetchSubscriptions();
            showMessage("Push notifications disabled.", true);
        } catch (e) {
            showMessage("Failed to disable: " + (e as Error).message, false);
        } finally {
            setActionLoading(false);
        }
    };

    const removeSubscription = async (endpoint: string) => {
        setActionLoading(true);
        try {
            await UnsubscribeWebPush(endpoint);
            await fetchSubscriptions();
            showMessage("Subscription removed.", true);
        } catch {
            showMessage("Failed to remove subscription.", false);
        } finally {
            setActionLoading(false);
        }
    };

    const sendTestNotification = async () => {
        setActionLoading(true);
        try {
            const result = await TestWebPush();
            showMessage(result.success ? "Test notification sent!" : "Failed: " + result.message, result.success);
        } catch {
            showMessage("Failed to send test notification.", false);
        } finally {
            setActionLoading(false);
        }
    };

    const showMessage = (text: string, success: boolean) => {
        setMessage({ text, success });
        setTimeout(() => setMessage(null), 3000);
    };

    const urlBase64ToUint8Array = (base64String: string) => {
        const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
        const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
        const rawData = window.atob(base64);
        return Uint8Array.from([...rawData].map((c) => c.charCodeAt(0)));
    };

    const isSubscribed = !!currentSubscription;

    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            {/* Navigation Tabs */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
                <nav className="flex space-x-4 px-6 py-3 border-b border-gray-200">
                    <Link to="/profile" className="px-3 py-2 text-sm font-medium rounded-md text-gray-700 hover:text-gray-900 hover:bg-gray-50">
                        <FontAwesomeIcon icon={["fad", "user"]} className="mr-2" />
                        Profile
                    </Link>
                    <Link to="/profile/data" className="px-3 py-2 text-sm font-medium rounded-md text-gray-700 hover:text-gray-900 hover:bg-gray-50">
                        <FontAwesomeIcon icon={["fad", "database"]} className="mr-2" />
                        Data
                    </Link>
                    <Link to="/profile/mfa" className="px-3 py-2 text-sm font-medium rounded-md text-gray-700 hover:text-gray-900 hover:bg-gray-50">
                        <FontAwesomeIcon icon={["fad", "shield-check"]} className="mr-2" />
                        MFA
                    </Link>
                    <Link to="/profile/security" className="px-3 py-2 text-sm font-medium rounded-md text-gray-700 hover:text-gray-900 hover:bg-gray-50">
                        <FontAwesomeIcon icon={["fad", "lock"]} className="mr-2" />
                        Security
                    </Link>
                    <Link to="/profile/notifications" className="px-3 py-2 text-sm font-medium rounded-md text-blue-600 bg-blue-50 border-b-2 border-blue-600">
                        <FontAwesomeIcon icon={["fad", "bell"]} className="mr-2" />
                        Notifications
                    </Link>
                </nav>
            </div>

            {/* Content */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-center justify-between mb-6">
                    <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                        <FontAwesomeIcon icon={["fad", "bell"]} className="mr-2 text-blue-500" />
                        Push Notifications
                    </h2>
                    {isSubscribed && (
                        <button
                            onClick={sendTestNotification}
                            disabled={actionLoading}
                            className="px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md transition-colors disabled:opacity-50"
                        >
                            <FontAwesomeIcon icon={["fad", "paper-plane"]} className="mr-1.5" />
                            Send Test
                        </button>
                    )}
                </div>

                {!supported && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                        <p className="text-sm text-yellow-800">
                            <FontAwesomeIcon icon={["fad", "exclamation-triangle"]} className="mr-2" />
                            Push notifications are not supported in this browser.
                        </p>
                    </div>
                )}

                {supported && (
                    <>
                        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200 mb-6">
                            <div className="flex items-center space-x-3">
                                <FontAwesomeIcon
                                    icon={["fad", isSubscribed ? "bell" : "bell-slash"]}
                                    className={`text-2xl ${isSubscribed ? "text-blue-600" : "text-gray-400"}`}
                                />
                                <div>
                                    <p className="font-medium text-gray-900">
                                        {isSubscribed ? "Push notifications are enabled" : "Push notifications are disabled"}
                                    </p>
                                    <p className="text-sm text-gray-600">
                                        {isSubscribed
                                            ? "You will receive login approval requests on this device."
                                            : "Enable to approve logins from other devices."}
                                    </p>
                                    {permission === "denied" && (
                                        <p className="text-sm text-red-600 mt-1">
                                            Notifications blocked in browser settings. Please allow them and try again.
                                        </p>
                                    )}
                                </div>
                            </div>
                            <button
                                onClick={isSubscribed ? disablePushNotifications : enablePushNotifications}
                                disabled={actionLoading || permission === "denied" || loading}
                                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                                    isSubscribed
                                        ? "bg-red-100 hover:bg-red-200 text-red-700"
                                        : "bg-blue-600 hover:bg-blue-700 text-white"
                                }`}
                            >
                                {actionLoading ? (
                                    <FontAwesomeIcon icon={["fad", "spinner"]} spin className="mr-2" />
                                ) : null}
                                {isSubscribed ? "Disable" : "Enable"}
                            </button>
                        </div>

                        {subscriptions.length > 0 && (
                            <div>
                                <h3 className="text-sm font-medium text-gray-700 mb-3">
                                    Registered Devices ({subscriptions.length})
                                </h3>
                                <div className="space-y-2">
                                    {subscriptions.map((sub) => (
                                        <div
                                            key={sub.id}
                                            className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200"
                                        >
                                            <div className="flex items-center space-x-3 min-w-0">
                                                <FontAwesomeIcon icon={["fad", "mobile-alt"]} className="text-blue-500 flex-shrink-0" />
                                                <span className="text-sm text-gray-600 truncate">{sub.endpoint || "(no endpoint)"}</span>
                                            </div>
                                            <button
                                                onClick={() => removeSubscription(sub.endpoint)}
                                                disabled={actionLoading}
                                                className="ml-3 flex-shrink-0 text-red-500 hover:text-red-700 text-sm disabled:opacity-50"
                                            >
                                                <FontAwesomeIcon icon={["fad", "trash"]} />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>

            {message && (
                <div
                    className={`fixed bottom-4 right-4 rounded-lg p-4 shadow-lg border ${
                        message.success ? "bg-green-50 border-green-200" : "bg-red-50 border-red-200"
                    }`}
                >
                    <div className="flex items-center">
                        <FontAwesomeIcon
                            icon={["fad", message.success ? "check-circle" : "exclamation-circle"]}
                            className={`mr-2 ${message.success ? "text-green-500" : "text-red-500"}`}
                        />
                        <p className={`text-sm font-medium ${message.success ? "text-green-800" : "text-red-800"}`}>
                            {message.text}
                        </p>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ProfileNotifications;
